import { useEffect } from "react";
import { useQuery } from "react-query";
import { fetchDashboardDeferred, fetchPartnerDashboardDeferred, fetchPlatformDashboardDeferred } from "../api/client";
import { useConsolidatedDashboardContext } from "./useConsolidatedDashboardContext";
import { useDashboardJobBootstrap } from "../context/DashboardJobBootstrapContext";
import { isDeferredDashboardMeta } from "./dashboardDeferredPoll";
import { shouldPrefetchDashboardPreview, shouldPrefetchPartnerDashboardPreview } from "../utils/dashboardDateRange";
import { bootstrapDashboardJob, bootstrapPartnerDashboardJob } from "../utils/dashboardJobBootstrap";
import {
  useDashboardBackgroundJobs,
  useDashboardJobsRegister,
} from "../context/DashboardJobsContext";
import { dashboardRouteLabel } from "../utils/dashboardJobLabels";
import { buildDashboardScopeKey } from "../utils/dashboardScopeKey";

/** Monta querystring a partir de um objeto de filtros */
const toQS = (obj = {}) => {
  const sp = new URLSearchParams();

  Object.entries(obj).forEach(([k, v]) => {
    if (v === null || v === undefined) return;

    if (Array.isArray(v)) {
      const arr = v.map((x) => (x ?? "").toString().trim()).filter((x) => x !== "");
      if (arr.length) sp.set(k, arr.join(","));
    } else {
      const s = String(v).trim();
      if (s) sp.set(k, s);
    }
  });

  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

async function fetchDashboardPreview(scope, subpath, params) {
  const previewParams = { ...params, preview: 1 };
  const previewUrl = `/dashboard/${scope}/${subpath}${toQS(previewParams)}`;
  const previewRes = await fetchDashboardDeferred(previewUrl);
  if (previewRes.httpStatus === 200 && previewRes.data) {
    return previewRes.data;
  }
  return null;
}

async function fetchPartnerDashboardPreview(scope, subpath, params) {
  const previewRes = await fetchPartnerDashboardDeferred(scope, subpath, { ...params, preview: 1 });
  if (previewRes.httpStatus === 200 && previewRes.data) {
    return previewRes.data;
  }
  return null;
}

async function fetchPlatformDashboardPreview(scope, subpath, params) {
  const previewRes = await fetchPlatformDashboardDeferred(scope, subpath, { ...params, preview: 1 });
  if (previewRes.httpStatus === 200 && previewRes.data) {
    return previewRes.data;
  }
  return null;
}

/**
 * Preview leve em query paralela + relatório completo (sync, deadline ou 202).
 * Polling de jobs fica centralizado no DashboardJobsContext — abas não bloqueiam navegação.
 */
export const useDashboard = (scope, subpath, params = {}, options = {}) => {
  const qs = toQS(params);
  const consolidatedCtx = useConsolidatedDashboardContext();
  const isConsolidated = Boolean(consolidatedCtx?.enabled);
  const isPlatform = consolidatedCtx?.variant === "platform";
  const dashboardPrefix = isPlatform ? "platform-dashboard" : "partner-dashboard";
  const bootstrapJobIdFromContext = useDashboardJobBootstrap();
  const bootstrapJobId = options.priorityJobId ?? bootstrapJobIdFromContext;
  const registerBackgroundJob = useDashboardJobsRegister();
  const { isJobCancelled, isScopeCancelled, hasActiveBackgroundJobs } = useDashboardBackgroundJobs();

  const scopeKey = buildDashboardScopeKey(
    scope,
    subpath,
    params,
    isConsolidated ? consolidatedCtx.dominioIds : []
  );
  const scopeCancelled = isScopeCancelled(scopeKey);

  const key = isConsolidated
    ? [dashboardPrefix, scope, subpath, params, consolidatedCtx.dominioIds, bootstrapJobId || null]
    : ["dashboard", scope, subpath, params, bootstrapJobId || null];

  const { select: userSelect, preview: enablePreview = true, alwaysPreview = false, ...queryOptions } = options;

  const consolidatedWorkspaceCount = isConsolidated
    ? consolidatedCtx.dominioIds?.length || consolidatedCtx.workspaces?.length || 0
    : 0;

  const prefetchPreview =
    enablePreview !== false &&
    !scopeCancelled &&
    (alwaysPreview ||
      (isConsolidated
        ? shouldPrefetchPartnerDashboardPreview(params.start, params.end, consolidatedWorkspaceCount)
        : shouldPrefetchDashboardPreview(params.start, params.end)));

  const previewKey = isConsolidated
    ? [`${dashboardPrefix}-preview`, scope, subpath, params, consolidatedCtx.dominioIds]
    : ["dashboard-preview", scope, subpath, params];

  const normalizedSelect = (resp) => {
    const inner = resp?.data ?? resp;

    const hasPaginationMeta =
      resp &&
      typeof resp === "object" &&
      !Array.isArray(resp) &&
      (resp.pagination !== undefined || resp.totalInDatabase !== undefined);

    let payload;
    if (hasPaginationMeta) {
      payload = Array.isArray(inner)
        ? { data: inner, pagination: resp.pagination, totalInDatabase: resp.totalInDatabase }
        : { ...(inner || {}), pagination: resp.pagination, totalInDatabase: resp.totalInDatabase };
    } else {
      payload = inner;
    }

    return userSelect ? userSelect(payload) : payload;
  };

  const query = useQuery(
    key,
    async () => {
      if (scopeCancelled) {
        return {
          data: null,
          deferred: false,
          jobMeta: null,
          userCancelled: true,
          scopeKey,
        };
      }

      if (isConsolidated) {
        const consolidatedParams = { ...params, dominio_ids: consolidatedCtx.dominioIds };
        const fetchDeferred = isPlatform ? fetchPlatformDashboardDeferred : fetchPartnerDashboardDeferred;

        if (bootstrapJobId) {
          const boot = await bootstrapPartnerDashboardJob(bootstrapJobId, {
            scope,
            subpath,
            params: consolidatedParams,
            variant: isPlatform ? "platform" : "partner",
          });
          if (boot.kind === "ready") {
            return { data: boot.payload, deferred: false, jobMeta: null, scopeKey };
          }
          if (boot.kind === "pending") {
            return {
              data: null,
              deferred: true,
              jobMeta: {
                jobId: boot.jobId,
                pollAfterMs: boot.pollAfterMs,
                status: "computing",
              },
              scopeKey,
            };
          }
        }

        const first = await fetchDeferred(scope, subpath, consolidatedParams);
        const firstBody = first?.data ?? first;

        if (first.httpStatus === 202 || isDeferredDashboardMeta(firstBody)) {
          const meta = isDeferredDashboardMeta(firstBody) ? firstBody : firstBody;
          if (meta?.jobId && isJobCancelled(meta.jobId)) {
            return { data: null, deferred: false, jobMeta: null, userCancelled: true, scopeKey };
          }
          return {
            data: null,
            deferred: true,
            jobMeta: meta,
            scopeKey,
          };
        }

        return { data: firstBody, deferred: false, jobMeta: null, scopeKey };
      }

      if (bootstrapJobId) {
        const boot = await bootstrapDashboardJob(bootstrapJobId, { scope, subpath, params });
        if (boot.kind === "ready") {
          return { data: boot.payload, deferred: false, jobMeta: null, scopeKey };
        }
        if (boot.kind === "pending") {
          return {
            data: null,
            deferred: true,
            jobMeta: {
              jobId: boot.jobId,
              pollAfterMs: boot.pollAfterMs,
              status: "computing",
            },
            scopeKey,
          };
        }
      }

      const url = `/dashboard/${scope}/${subpath}${qs}`;
      const first = await fetchDashboardDeferred(url);
      const firstBody = first?.data ?? first;

      if (first.httpStatus === 202 || isDeferredDashboardMeta(firstBody)) {
        const meta = isDeferredDashboardMeta(firstBody) ? firstBody : firstBody;
        if (meta?.jobId && isJobCancelled(meta.jobId)) {
          return { data: null, deferred: false, jobMeta: null, userCancelled: true, scopeKey };
        }
        return {
          data: null,
          deferred: true,
          jobMeta: meta,
          pagination: null,
          totalInDatabase: null,
          scopeKey,
        };
      }

      return {
        data: firstBody,
        deferred: false,
        jobMeta: null,
        pagination: first.pagination,
        totalInDatabase: first.totalInDatabase,
        scopeKey,
      };
    },
    {
      enabled: !scopeCancelled,
      staleTime: 120_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      keepPreviousData: true,
      retry: (failureCount, error) => {
        const status = error?.response?.status ?? error?.status;
        if (status === 503 || status === 504) return false;
        if (String(error?.message || "") === "dashboard_job_timeout") return false;
        return failureCount < 1;
      },
      ...queryOptions,
    }
  );

  useEffect(() => {
    const jobMeta = query.data?.jobMeta;
    if (!query.data?.deferred || !jobMeta?.jobId || query.isPreviousData) return;
    if (isJobCancelled(jobMeta.jobId)) return;
    registerBackgroundJob(jobMeta, {
      start: params.start,
      end: params.end,
      title: dashboardRouteLabel(jobMeta.route),
    });
  }, [
    query.data?.deferred,
    query.data?.jobMeta,
    query.isPreviousData,
    params.start,
    params.end,
    registerBackgroundJob,
    isJobCancelled,
  ]);

  const isPreviousData = Boolean(query.isPreviousData);
  const userCancelled = Boolean(query.data?.userCancelled) || scopeCancelled;
  const isComputing = Boolean(query.data?.deferred) && !isPreviousData && !userCancelled;
  const shouldLoadPreview =
    enablePreview !== false &&
    !scopeCancelled &&
    (prefetchPreview || isComputing);

  const previewQuery = useQuery(
    previewKey,
    () =>
      isConsolidated
        ? (isPlatform ? fetchPlatformDashboardPreview : fetchPartnerDashboardPreview)(scope, subpath, {
            ...params,
            dominio_ids: consolidatedCtx.dominioIds,
          })
        : fetchDashboardPreview(scope, subpath, params),
    {
      enabled: shouldLoadPreview,
      staleTime: 120_000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const isBackgroundMode = isComputing && hasActiveBackgroundJobs;
  const mainPending =
    query.isLoading || (query.isFetching && !query.data?.data && !isComputing && !isBackgroundMode);

  const fullRaw = !isPreviousData && query.data?.data && !isComputing ? query.data.data : null;
  const previewRaw = shouldLoadPreview && previewQuery.data ? previewQuery.data : null;
  const usePreview = !fullRaw && Boolean(previewRaw);

  const rawData = isPreviousData ? null : fullRaw ?? (usePreview ? previewRaw : null);
  const selected =
    rawData != null
      ? normalizedSelect({
          data: rawData,
          pagination: query.data?.pagination,
          totalInDatabase: query.data?.totalInDatabase,
        })
      : undefined;

  const pollError = !isPreviousData ? query.data?.pollError : null;
  const hasDisplayData = selected != null;
  const isPreview = usePreview && !fullRaw;
  const previewPending =
    shouldLoadPreview &&
    !hasDisplayData &&
    (previewQuery.isLoading || previewQuery.isFetching) &&
    !previewQuery.isError;
  const awaitingPreview = previewPending;

  return {
    ...query,
    data: selected,
    error: query.error || pollError || null,
    isDeferred: isComputing && !isBackgroundMode,
    isComputing,
    isBackgroundJob: isBackgroundMode,
    isPreview,
    isAwaitingPreview: awaitingPreview,
    userCancelled,
    jobMeta: query.data?.jobMeta || null,
    isLoading:
      !hasDisplayData &&
      !userCancelled &&
      (awaitingPreview ||
        (!isComputing && !isBackgroundMode && mainPending) ||
        isPreviousData),
    isFetchingFull:
      !isBackgroundMode &&
      !userCancelled &&
      (mainPending && Boolean(isPreview)),
  };
};
